import { LanCandidateRefreshService, TransportManager } from "@econ-v1/domain";
import { canonicalHttpsOrigin } from "./http-origin.js";
import { ManagedHomeNodeClient } from "./managed-home-node-client.js";
/**
 * Coordinates the direct-ladder transport manager (browser-trusted LAN
 * HTTPS, hash-pinned LAN WebTransport, WAN HTTPS/WSS — ticket #1823) with
 * the long-lived client seam. Scheduling is deliberately external: callers
 * invoke reconcile and refreshCandidates. The `refresh` callback may return
 * any direct-ladder candidate (including WebTransport); candidates loaded
 * from `IdentityPort` local storage remain HTTP-only, since that persisted
 * connection metadata has no certificate-hash fields to persist.
 *
 * Also the single consolidated policy owner for WAN WebRTC direct
 * promotion/demotion (ticket #1826, `webrtc` option above) — the LAN/WAN
 * hysteresis this class already implements for the HTTP rungs is reused
 * as-is for recovering from `webrtc-direct` to LAN, rather than duplicated.
 */
export class HttpTransportSupervisor {
    client;
    #identity;
    #manager;
    #now;
    #promotionSuccesses;
    #promotionStabilityMs;
    #transportFailureRecoveryFloorMs;
    #transportHandshakeGraceMs;
    #refresh;
    #seedCandidates;
    #webrtc;
    #closed = false;
    #closeRun;
    #loadedPersistedCandidates = false;
    #lanFirstSuccessAt;
    #lanProbeKey;
    #lanSuccesses = 0;
    #operation = Promise.resolve();
    #persistedCandidates = [];
    #reconcileRun;
    #rerunRequested = false;
    #refreshRun;
    #refreshAbort;
    /**
     * True after `#refreshCandidates` last threw (ticket #1867's real-harness
     * gate: a brand-new device session's very first `transport.candidate-
     * refresh` tick — scheduled `dueAt: now` at kernel construction, per
     * `system/pwa/src/kernel-runtime.ts` — races ahead of pairing and always
     * fails with "Client device session is not established"; the next
     * scheduled attempt is a fixed `transportRefreshCadenceMs` — 300s by
     * default — later). `reconcile()` below self-heals from this: while a
     * refresh is outstanding-failed, its own 10s cadence (`transport.
     * reconcile`) retries the candidate fetch instead of a bare reconcile, so
     * a freshly-paired device is offered its real LAN candidate within one
     * `transportRecoveryCadenceMs` tick of the session actually existing,
     * rather than waiting out the full refresh cadence.
     */
    #refreshFailed = false;
    #lastTransportFailureAt;
    /**
     * Set to `#now()` right after the fallback branch of `#reconcile()`
     * activates a fresh client that has NOT yet reached "connected" (its
     * handshake is still in flight — see `#transportHandshakeGraceMs`'s doc
     * comment). Cleared implicitly by elapsed time: once a client reaches
     * "connected" the guard that reads this field is never reached again for
     * it (the dedicated LAN/WAN-tier branches above intercept first), and if
     * it later drops back offline after a real connected period, the stored
     * timestamp is old enough that the grace window has already elapsed.
     */
    #pendingActivationAt;
    constructor(options) {
        this.#manager = options.manager;
        this.#identity = options.identity;
        this.#now = options.now ?? (() => Date.now());
        this.#promotionSuccesses = Math.max(1, options.promotionSuccesses ?? 2);
        this.#promotionStabilityMs = Math.max(0, options.promotionStabilityMs ?? 10_000);
        this.#transportFailureRecoveryFloorMs = Math.max(0, options.transportFailureRecoveryFloorMs ?? 1_000);
        this.#transportHandshakeGraceMs = Math.max(0, options.transportHandshakeGraceMs ?? 5_000);
        this.#refresh = options.refresh;
        this.#seedCandidates = (options.initialCandidates ?? []).map((candidate) => ({ ...candidate }));
        if (options.initialCandidates !== undefined) {
            this.#manager.replaceCandidates(this.#orderedCandidates([]));
        }
        this.client = new ManagedHomeNodeClient(this.#manager, {
            onTransportFailure: () => this.#handleTransportFailure(),
            ...(options.initial === undefined ? {} : { initial: options.initial }),
            ...(options.paymentHandler === undefined ? {} : { paymentHandler: options.paymentHandler }),
        });
        this.#webrtc = options.webrtc?.(this.client);
    }
    /**
     * Debounced entry point for `ManagedHomeNodeClient`'s `onTransportFailure`
     * callback (see `#transportFailureRecoveryFloorMs`'s doc comment). Explicit
     * `reconcile()` callers (the scheduled `transport.reconcile` tick) are
     * unaffected — this floor only governs the reactive, failure-triggered
     * path, which is the one capable of spinning at machine speed.
     */
    #handleTransportFailure() {
        const now = this.#now();
        if (this.#lastTransportFailureAt !== undefined &&
            now - this.#lastTransportFailureAt < this.#transportFailureRecoveryFloorMs) {
            return;
        }
        this.#lastTransportFailureAt = now;
        void this.reconcile();
    }
    get state() {
        return this.client.state;
    }
    reconcile() {
        if (this.#closed)
            return Promise.resolve();
        // A previously-failed candidate refresh (see `#refreshFailed`'s doc
        // comment) is retried on this method's own, much shorter cadence instead
        // of waiting for the next scheduled `refreshCandidates()` tick.
        if (this.#refreshFailed && this.#refresh !== undefined && this.#refreshRun === undefined) {
            return this.refreshCandidates();
        }
        if (this.#reconcileRun !== undefined) {
            if (this.state.status === "offline")
                this.#rerunRequested = true;
            return this.#reconcileRun;
        }
        const run = this.#enqueue(async () => await this.#reconcile());
        this.#reconcileRun = run;
        void run.finally(() => {
            if (this.#reconcileRun !== run)
                return;
            this.#reconcileRun = undefined;
            if (!this.#closed && this.#rerunRequested) {
                this.#rerunRequested = false;
                void this.reconcile();
            }
        }).catch(() => undefined);
        return run;
    }
    refreshCandidates() {
        if (this.#closed)
            return Promise.resolve();
        if (this.#refreshRun !== undefined)
            return this.#refreshRun;
        const abort = new AbortController();
        this.#refreshAbort = abort;
        const run = this.#enqueue(async () => {
            if (this.#closed)
                return;
            await this.#refreshCandidates(abort.signal);
            if (!this.#closed)
                await this.#reconcile();
        });
        this.#refreshRun = run;
        void run.finally(() => {
            if (this.#refreshRun === run)
                this.#refreshRun = undefined;
            if (this.#refreshAbort === abort)
                this.#refreshAbort = undefined;
        }).catch(() => undefined);
        return run;
    }
    /**
     * Restarts ICE on the promoted WAN WebRTC direct connection, if any — the
     * caller-driven recovery primitive for an explicit network-change signal
     * (e.g. the browser's own `online` event, or a Wi-Fi/cellular handoff).
     * A no-op (`false`) when no WebRTC port is configured or nothing is
     * currently promoted — the LAN/WAN baseline rungs have their own
     * independent reconnect logic and never go through this. Never throws.
     */
    async restartIce() {
        if (this.#webrtc === undefined)
            return false;
        return await this.#webrtc.restartIce();
    }
    close() {
        if (this.#closeRun !== undefined)
            return this.#closeRun;
        this.#closed = true;
        this.#refreshAbort?.abort();
        this.#manager.abort();
        this.client.close();
        const webrtcClose = this.#webrtc?.close().catch(() => undefined) ?? Promise.resolve();
        const run = this.#operation.then(() => undefined, () => undefined).then(async () => await webrtcClose);
        this.#closeRun = run;
        return run;
    }
    #enqueue(task) {
        const run = this.#operation.then(task, task);
        this.#operation = run.catch(() => undefined);
        return run;
    }
    async #reconcile() {
        if (this.#closed)
            return;
        if (!this.#loadedPersistedCandidates) {
            this.#loadedPersistedCandidates = true;
            await this.#loadPersistedCandidates().catch(() => undefined);
            if (this.#closed)
                return;
        }
        // A pending handshake marker only ever guards the fallback branch below
        // against superseding a client that has not yet reported "connected"
        // since ITS activation. Once we are actually connected — however this
        // reconcile got here — that marker is stale (either this activation
        // already finished its handshake, or a later one has); clearing it here
        // means a subsequent genuine drop-and-retry is never held back by a
        // grace window measured from a long-past, already-settled activation.
        if (this.state.status === "connected")
            this.#pendingActivationAt = undefined;
        if (this.state.status === "connected" &&
            (this.state.path === "lan-direct" || this.state.path === "lan-webtransport") &&
            this.#selectedCandidateIsCurrentLan()) {
            return;
        }
        if (this.state.status === "connected" && this.#isWanTierPath(this.state.path)) {
            // Only attempt WAN WebRTC direct promotion from the bare HTTP/WSS
            // baseline, never from an already-promoted `webrtc-direct` — that
            // rung already outranks bare WAN, so there is nothing to promote to
            // here except a returning LAN candidate (handled below either way).
            const wasWanBaseline = this.state.path === "wan-direct";
            try {
                const lan = await this.#manager.connect({ higherPriorityThan: "wan-direct" });
                if (this.#closed) {
                    await closeClient(lan);
                    return;
                }
                if (this.state.status !== "connected" || !this.#isWanTierPath(this.state.path)) {
                    this.#resetPromotion();
                    await closeClient(lan);
                    this.#rerunRequested = true;
                    return;
                }
                const selected = this.#manager.selectedCandidate;
                const probeKey = selected === undefined ? undefined : `${selected.id}\u0000${selected.address}`;
                const now = this.#now();
                if (probeKey === undefined || probeKey !== this.#lanProbeKey) {
                    this.#lanProbeKey = probeKey;
                    this.#lanFirstSuccessAt = now;
                    this.#lanSuccesses = 1;
                }
                else if (this.#lanFirstSuccessAt !== undefined &&
                    now - this.#lanFirstSuccessAt >= this.#promotionStabilityMs) {
                    this.#lanSuccesses += 1;
                }
                if (this.#lanSuccesses < this.#promotionSuccesses) {
                    await closeClient(lan);
                    await this.#replaceBootstrapWan();
                    if (wasWanBaseline)
                        await this.#attemptWebrtcPromotion();
                    return;
                }
                this.#resetPromotion();
                await this.client.replace(lan);
                this.#manager.markConnected(selected?.path ?? "lan-direct");
            }
            catch {
                this.#resetPromotion();
                await this.#replaceBootstrapWan();
                if (wasWanBaseline)
                    await this.#attemptWebrtcPromotion();
            }
            return;
        }
        // A previous fallback activation may still be mid-handshake (its dial
        // already resolved, but its own transport has not yet reported
        // "connected") — see `#transportHandshakeGraceMs`'s doc comment. Give it
        // the rest of its grace window instead of unconditionally superseding it
        // here; a genuinely hung or rejected handshake is still replaced once
        // that window elapses.
        if (this.#pendingActivationAt !== undefined &&
            this.#now() - this.#pendingActivationAt < this.#transportHandshakeGraceMs) {
            return;
        }
        this.#resetPromotion();
        try {
            const next = await this.#manager.connect();
            if (this.#closed) {
                await closeClient(next);
                return;
            }
            // Snapshotted BEFORE `replace()` activates it: a candidate whose dial
            // resolves early — `probeLanCandidate`'s `lan-direct` path resolves as
            // soon as the plain-HTTP origin-proof check passes, well before its
            // `HttpWsHomeNodeClient`'s own WebSocket handshake finishes — reports
            // "offline" here even though the manager already picked it as the
            // winning candidate. A candidate whose dial only resolves once fully
            // ready (the WAN path's `client.ready(signal)`, or any already-settled
            // test double) reports "connected" here instead, and never engages
            // the grace guard above.
            const stillHandshaking = next.state.status !== "connected";
            await this.client.replace(next);
            this.#pendingActivationAt = stillHandshaking ? this.#now() : undefined;
        }
        catch {
            this.#manager.markOffline();
        }
    }
    async #refreshCandidates(signal) {
        if (this.#refresh !== undefined) {
            let candidates;
            try {
                candidates = await this.#refresh(this.client, signal);
            }
            catch (error) {
                this.#refreshFailed = true;
                throw error;
            }
            this.#refreshFailed = false;
            if (this.#closed || signal.aborted)
                return;
            this.#replacePersistedCandidates(candidates);
            this.#resetPromotion();
            return;
        }
        if (this.#identity === undefined)
            return;
        const refresh = new LanCandidateRefreshService({ identity: this.#identity });
        try {
            await refresh.refresh(this.client);
        }
        catch (error) {
            this.#refreshFailed = true;
            throw error;
        }
        this.#refreshFailed = false;
        if (this.#closed || signal.aborted)
            return;
        await this.#loadPersistedCandidates();
        this.#resetPromotion();
    }
    async #loadPersistedCandidates() {
        if (this.#identity === undefined)
            return;
        const metadata = await this.#identity.loadConnectionMetadata();
        if (metadata?.homeNodeId === undefined) {
            this.#replacePersistedCandidates([]);
            return;
        }
        const candidates = [];
        for (const address of metadata.directAddresses) {
            const candidate = validCandidate({ address, id: metadata.homeNodeId, path: "lan-direct" });
            if (candidate !== undefined)
                candidates.push(candidate);
        }
        if (metadata.wanOrigin !== undefined) {
            const candidate = validCandidate({ address: metadata.wanOrigin, id: metadata.homeNodeId, path: "wan-direct" });
            if (candidate !== undefined)
                candidates.push(candidate);
        }
        this.#replacePersistedCandidates(candidates);
    }
    #replacePersistedCandidates(candidates) {
        const valid = candidates.flatMap((candidate) => {
            const next = validCandidate(candidate);
            return next === undefined ? [] : [next];
        });
        const seedTrust = new Set(this.#seedCandidates
            .filter((candidate) => identityBindingOf(candidate) !== "bootstrap")
            .map((candidate) => candidate.id));
        const persistedTrust = new Set(valid.map((candidate) => candidate.id));
        this.#persistedCandidates = persistedTrust.size > 1 ||
            (seedTrust.size === 1 && [...persistedTrust].some((id) => !seedTrust.has(id)))
            ? []
            : valid;
        this.#manager.replaceCandidates(this.#orderedCandidates(this.#persistedCandidates));
    }
    #orderedCandidates(persisted) {
        const trustedSeeds = this.#seedCandidates.filter((candidate) => identityBindingOf(candidate) !== "bootstrap");
        const bootstrapFallbacks = this.#seedCandidates.filter((candidate) => identityBindingOf(candidate) === "bootstrap");
        const hasTrustedWan = [...persisted, ...trustedSeeds].some((candidate) => candidate.path === "wan-direct");
        return [
            ...persisted,
            ...trustedSeeds,
            ...(hasTrustedWan ? [] : bootstrapFallbacks),
        ];
    }
    #selectedCandidateIsCurrentLan() {
        const selected = this.#manager.selectedCandidate;
        if (selected === undefined || (selected.path !== "lan-direct" && selected.path !== "lan-webtransport")) {
            return false;
        }
        return this.#orderedCandidates(this.#persistedCandidates).some((candidate) => candidate.path === selected.path &&
            candidate.id === selected.id &&
            candidate.address === selected.address);
    }
    /**
     * Skipped entirely while `webrtc-direct` is the active path (ticket
     * #1865, the transient-clobber window flagged in the N5 review): the
     * `TransportManager` this reads `selectedCandidate` from never learns
     * about `webrtc-direct` (see the class doc comment above), so once a
     * bootstrap WAN connection has been promoted to `webrtc-direct` the
     * manager's `selectedCandidate` still shows that stale bootstrap-bound
     * WAN candidate. Without this guard, the very next reconcile tick that
     * lands here (e.g. from the LAN-probe branches above, whenever a
     * canonical non-bootstrap WAN candidate is already known) would dial a
     * fresh plain WAN HTTPS/WSS connection and `client.replace()` the
     * healthy, already-promoted `webrtc-direct` session with it — clobbering
     * a working connection for no reachability gain. `webrtc-direct`
     * recovers to a canonical WAN/LAN candidate exactly like bare
     * `wan-direct` already does via the LAN-probe branches above; it never
     * needs this bootstrap-specific replacement path.
     */
    async #replaceBootstrapWan() {
        if (this.state.path === "webrtc-direct")
            return;
        const selected = this.#manager.selectedCandidate;
        if (identityBindingOf(selected) !== "bootstrap" ||
            !this.#orderedCandidates(this.#persistedCandidates).some((candidate) => candidate.path === "wan-direct" &&
                identityBindingOf(candidate) !== "bootstrap")) {
            return;
        }
        try {
            const next = await this.#manager.connect({ path: "wan-direct" });
            if (this.#closed) {
                await closeClient(next);
                return;
            }
            await this.client.replace(next);
        }
        catch {
            await this.client.disconnect();
        }
    }
    #resetPromotion() {
        this.#lanFirstSuccessAt = undefined;
        this.#lanProbeKey = undefined;
        this.#lanSuccesses = 0;
    }
    /**
     * `webrtc-direct` outranks bare `wan-direct` but sits below both LAN
     * rungs (ticket #1826's ladder), so every reconcile decision that treats
     * "we are on the WAN baseline, possibly needing LAN recovery" must treat
     * an already-promoted WebRTC connection the same way — otherwise a
     * routine reconcile tick would keep replacing a healthy promoted
     * connection with a fresh bare-WAN dial.
     */
    #isWanTierPath(path) {
        return path === "wan-direct" || path === "webrtc-direct";
    }
    /**
     * Attempts WAN WebRTC direct promotion once reconcile has confirmed there
     * is no better LAN candidate available. Re-checks state because
     * `#replaceBootstrapWan` above may have changed it asynchronously; a
     * failed or already-in-flight attempt is never an error here — the
     * `WebRtcPromotionPort` contract never throws.
     */
    async #attemptWebrtcPromotion() {
        if (this.#webrtc === undefined || this.#closed)
            return;
        if (this.state.status !== "connected" || this.state.path !== "wan-direct")
            return;
        await this.#webrtc.attemptPromotion().catch(() => undefined);
    }
}
async function closeClient(client) {
    if (client.close === undefined)
        return;
    await Promise.resolve(client.close()).catch(() => undefined);
}
/**
 * `identityBinding` only exists on `HttpHomeNodeCandidate` — a WebTransport
 * candidate is always sourced from the authenticated candidates endpoint, so
 * it is never a pre-trust "bootstrap" candidate (ticket #1823).
 */
function identityBindingOf(candidate) {
    return candidate === undefined || candidate.path === "lan-webtransport" ? undefined : candidate.identityBinding;
}
function validCandidate(candidate) {
    if (candidate.id.trim() !== candidate.id || candidate.id === "")
        return undefined;
    try {
        return { ...candidate, address: canonicalHttpsOrigin(candidate.address) };
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=http-transport-supervisor.js.map